import { useState, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/services/api';

interface ProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const VOICE_GROUPS = [
  { value: '', label: 'Geen stemgroep' },
  { value: 'SOPRAAN', label: 'Sopraan' },
  { value: 'ALT', label: 'Alt' },
  { value: 'TENOR', label: 'Tenor' },
  { value: 'BAS', label: 'Bas' },
];

export function ProfileModal({ isOpen, onClose }: ProfileModalProps) {
  const { user } = useAuth();
  const [voiceGroup, setVoiceGroup] = useState(user?.voiceGroup || '');
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState('');
  const [photoPreview, setPhotoPreview] = useState(user?.photo || '');
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (!isOpen) return null;

  const handleSave = async () => {
    setIsLoading(true);
    setError('');

    try {
      await api.updateProfile({
        voiceGroup: voiceGroup || null,
      });
      onClose();
      window.location.reload(); // Refresh to update user data
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Opslaan mislukt');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Alleen afbeeldingen zijn toegestaan');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Afbeelding mag maximaal 5MB zijn');
      return;
    }

    setIsUploading(true);
    setError('');

    try {
      const result = await api.uploadProfilePhoto(file);
      setPhotoPreview(result.photo);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload mislukt');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-bg-elevated rounded-xl shadow-xl border border-subtle w-full max-w-md p-6">
        <h2 className="text-xl font-bold text-text-primary mb-6">Profiel bewerken</h2>

        {error && (
          <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded-lg text-sm mb-4">
            {error}
          </div>
        )}

        {/* Photo */}
        <div className="flex flex-col items-center mb-6">
          <div className="w-24 h-24 rounded-full overflow-hidden bg-zzing-purple-500/20 flex items-center justify-center mb-3">
            {photoPreview ? (
              <img
                src={photoPreview}
                alt={user?.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <span className="text-3xl text-zzing-purple-500 font-semibold">
                {user?.name.charAt(0).toUpperCase()}
              </span>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handlePhotoChange}
            className="hidden"
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
            className="text-sm text-zzing-purple-500 hover:text-zzing-purple-600 disabled:opacity-50"
          >
            {isUploading ? 'Uploaden...' : 'Foto wijzigen'}
          </button>
        </div>

        {/* Name (read-only) */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-text-secondary mb-2">
            Naam
          </label>
          <input
            type="text"
            value={user?.name || ''}
            disabled
            className="w-full px-4 py-2 border border-subtle rounded-lg bg-bg-base text-text-muted"
          />
          <p className="text-xs text-text-muted mt-1">
            Neem contact op met de beheerder om je naam te wijzigen
          </p>
        </div>

        {/* Voice Group */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-text-secondary mb-2">
            Stemgroep
          </label>
          <select
            value={voiceGroup}
            onChange={(e) => setVoiceGroup(e.target.value)}
            className="w-full px-4 py-2 border border-subtle rounded-lg focus:ring-2 focus:ring-zzing-purple-500 focus:border-transparent outline-none bg-bg-elevated text-text-primary"
          >
            {VOICE_GROUPS.map(group => (
              <option key={group.value} value={group.value}>
                {group.label}
              </option>
            ))}
          </select>
        </div>

        {/* Buttons */}
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-subtle rounded-lg text-text-secondary hover:bg-bg-elevated-hover transition"
          >
            Annuleren
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={isLoading}
            className="flex-1 px-4 py-2 bg-zzing-purple-500 text-white rounded-lg hover:bg-zzing-purple-600 transition disabled:opacity-50"
          >
            {isLoading ? 'Opslaan...' : 'Opslaan'}
          </button>
        </div>
      </div>
    </div>
  );
}
